/** @format */
import isPropValid from '@emotion/is-prop-valid';
import {
    Button,
    ButtonSize,
    ButtonVariant,
} from '@mpb-com/feather/components/atoms/button';
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent, ReactNode } from 'react';
import styled from 'styled-components';

import { MediaQuery, useMediaQuery } from '@toucan/client/global/application';

import { styles } from './confirm-modal.styles';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Legacy code: do not use as an example.
export interface ConfirmModalProps {
    /**
     * Child elements
     */
    children?: ReactNode;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * The message to display on the modal
     *
     * @default "Are you sure?"
     */
    message?: string | ReactNode;

    /**
     * The text shown in the confirm button
     *
     * @default "Yes"
     */
    confirmText?: string;

    /**
     * The text shown in the cancel button
     *
     * @default "No"
     */
    cancelText?: string;

    /**
     * A callback fired when the user confirms they are sure
     */
    onConfirm?: () => void;

    /**
     * A callback fired when the user cancels
     */
    onCancel?: () => void;
}

const StyledConfirmModal = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const ConfirmModal: FunctionComponent<ConfirmModalProps> = ({
    message,
    onCancel,
    onConfirm,
    cancelText,
    confirmText,
    ...otherProps
}: ConfirmModalProps) => {
    const { t } = useTranslation();

    const isLargeTablet = useMediaQuery(MediaQuery.LARGE_TABLET);
    const buttonSize = isLargeTablet ? ButtonSize.SMALL : ButtonSize.MEDIUM;

    const messageWithDefault = message ?? <p>{t('Are you sure?')}</p>;

    return (
        <StyledConfirmModal {...otherProps}>
            <div>{messageWithDefault}</div>
            <div className="buttons">
                {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                {onCancel && (
                    <Button
                        variant={ButtonVariant.TERTIARY}
                        onClick={onCancel}
                        size={buttonSize}
                        data-testid="confirm-modal__no"
                    >
                        {cancelText ?? t('No')}
                    </Button>
                )}
                {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                {onConfirm && (
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        onClick={onConfirm}
                        size={buttonSize}
                        data-testid="confirm-modal__yes"
                    >
                        {confirmText ?? t('Yes')}
                    </Button>
                )}
            </div>
        </StyledConfirmModal>
    );
};
